/* WRAPPED IN AN IIFE BECAUSE THE STOCK PAGE NOW LOADS EVERY APP AT ONCE (15/08/2026).
   These were four separate pages, so each could declare `escapeHtml`, `spinnerHtml`,
   `MONTHS` and friends at the top level harmlessly. On one page they share ONE global
   scope, and a second `const escapeHtml` is a SyntaxError that kills the WHOLE script -
   so `customElements.define` never ran and every tab after Stock check sat on its
   spinner for ever. Nothing here is used across files; the wrapper simply stops them
   seeing each other. Same shape as the `money` collision in the sync's inlined modules. */
(function () {
/* Staff stock check.
 *
 * One custom element, <stock-check>, posting to the published, secret-gated n8n
 * "Stock Ops" webhook carried on its data attributes. The webhook holds the
 * EposNow credential; nothing here ever sees it.
 *
 * Shape of the job it is built for: a member of staff walks the shop with a
 * tablet, drills down to the section they are standing at, and taps counts up or
 * down against a list of names. Navigation mirrors the till's own category tree
 * (Accessories > Dice > Maven Games) because that tree is how the shop is
 * physically laid out, so drilling down is the same movement as walking over.
 *
 * A node is never purely a folder or purely a section - "Dice" holds four
 * products of its own AND three sub-sections - so every level renders both.
 *
 * Two rules the till forces on us:
 *  - Rapid taps are coalesced into ONE write per product (see SAVE_DELAY),
 *    otherwise tapping +5 sends five separate stock writes.
 *  - Every write carries the value the tablet last believed, so the server can
 *    refuse it if a real sale landed mid-count rather than silently eating it.
 *
 * Deploy note (05/08/2026): if this asset starts returning 504 from Shopify's
 * CDN after a push, re-pushing the identical bytes will NOT fix it - the URL
 * carries a content hash, so the same bytes resolve to the same cached error.
 * Change the file's contents (even a comment) to mint a fresh versioned URL.
 */

const SAVE_DELAY = 900; // ms of quiet before a tapped row is written to the till
const SEARCH_DELAY = 300;
const MIN_SEARCH = 2;

// The till rejects counts outside this range as a mis-key guard. Checking here
// too means a fat-fingered number is refused instantly instead of being retried
// forever - a value the server will never accept is not a connection problem.
const MAX_COUNT = 9999;

// Stop retrying eventually. Whatever the cause, something that has failed this
// many times is not going to fix itself, and staff need telling rather than a
// spinner running down the battery.
const MAX_RETRY_ATTEMPTS = 6;

function countProblem(value) {
  if (!Number.isFinite(value) || !Number.isInteger(value)) return 'Whole numbers only.';
  if (value < 0) return "A count can't be below zero.";
  if (value > MAX_COUNT) return `That's over ${MAX_COUNT} \u2014 check the number.`;
  return null;
}

async function postOp(url, secret, operation, payload, signal) {
  const response = await (window.dcFetch || fetch)(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(Object.assign({ secret: secret, operation: operation }, payload)),
    signal: signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
  if (data && data.error) throw new Error(data.error);
  return data;
}

// Some products carry several barcodes in one field - different printings, a
// relabelled edition, a multipack. Palm Pals 5in has fourteen. Show the first
// and put the rest behind a chip, or a wall of digits pushes the row off screen.
function splitCodes(value) {
  return String(value == null ? '' : value)
    .split(/[,;]/)
    .map((code) => code.trim())
    .filter(Boolean);
}

function spinnerHtml(label) {
  return `<p class="stock-check__loading" role="status"><span class="stock-check__spinner" aria-hidden="true"></span>${escapeHtml(
    label,
  )}</p>`;
}

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ordinalSuffix(day) {
  if (day > 3 && day < 21) return 'th'; // 11th, 12th, 13th
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
}

// "5th Aug 2026" - a date staff read at a glance, not an ISO string.
function formatCheckedAt(value) {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return '';
  return `${when.getDate()}${ordinalSuffix(when.getDate())} ${MONTHS[when.getMonth()]} ${when.getFullYear()}`;
}

// HOW LONG A CHECK COUNTS FOR. A row goes green when somebody last confirmed it
// within this many days, so walking a section shows at a glance what still needs
// looking at. The date itself comes from the server, per product and shared by
// everyone - the local progress below is a different thing entirely, and was
// never visible to the next person or the next day.
const RECENT_DAYS = 7;

// THE PICTURE CELL IS SHARED with the other staff pages (`staff-pictures.js`), so a product
// with several pictures shows the same count and opens the same gallery everywhere. The empty
// frame for a product with none is kept, for the reason it always existed: this page is walked
// down a shelf on a tablet, and a list that does not line up is one a thumb mis-taps.
function imageHtml(item) {
  return window.StaffPictures.html(item, {
    className: 'stock-check__thumb', lazy: true, title: item && item.name,
  });
}

function isRecentlyChecked(value) {
  if (!value) return false;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return false;
  // A date in the future is a clock disagreeing with itself, not a check that
  // has not happened yet - treat it as recent rather than making a row that can
  // never go green.
  return Date.now() - when.getTime() < RECENT_DAYS * 24 * 60 * 60 * 1000;
}

// ONE TILL RECORD, TWO FIGURES (Jonathan, 26/08/2026). A master holds sealed
// wholes in `stock` and loose units in `volume`, and until now only the first
// could be counted - so walking a booster-box shelf left half of every master's
// record exactly as it was.
//
// `volumeOfSale` IS what makes a product a master. It is the project's rule
// everywhere else and it is the rule here: not the word "Box" in a name, not
// whether a sub happens to be nameable.
function isMaster(item) {
  return Number(item && item.volumeOfSale) > 0;
}

// What each figure is called on the row (Jonathan, 26/08/2026). His words, and
// they say the thing that matters: the top figure is the record being counted and
// the bottom one is the product whose stock lives on it. "Sealed / Loose" was my
// first attempt and describes the contents rather than the relationship, which is
// what a counter needs to know.
const PART_LABEL = { whole: 'Master', unit: 'Subproduct' };

// A BOOSTER BOX SAYS WHAT IT IS (Jonathan, 24/09/2026): "Boxes" and "Boosters", and a
// booster box is a master whose name ends with the word "box". Any other master - a pack
// of card pages sold singly - keeps Master / Subproduct.
const BOOSTER_BOX_LABEL = { whole: 'Boxes', unit: 'Boosters' };

function isBoosterBox(item) {
  return /(^|[^a-z])box$/i.test(String((item && item.name) || '').trim());
}

// The figure a part is showing, and the field it comes back in.
function partValue(item, part) {
  return part === 'unit' ? Number(item.volume) || 0 : Number(item.stock) || 0;
}

// One stepper: a label, minus, the number, plus.
//
// The label sits LEFT OF THE MINUS BUTTON (his instruction) so the eye reads
// "Sealed 16" across the row rather than having to work out which line is which.
// A master's sub is named in the aria-label and the tooltip, because the visible
// label has to stay short enough not to push the controls off a tablet.
function figureHtml(item, part) {
  const master = isMaster(item);
  const label = master ? (isBoosterBox(item) ? BOOSTER_BOX_LABEL : PART_LABEL)[part] : '';
  const of = part === 'unit'
    ? ((item.sub && item.sub.name) || `loose units of ${item.name}`)
    : item.name;
  return `
    <div class="stock-check__figure" data-part="${part}"${
      master ? ` title="${escapeHtml(of)}"` : ''
    }>
      ${label ? `<span class="stock-check__part">${escapeHtml(label)}</span>` : ''}
      <button type="button" class="stock-check__step" data-step="-1" aria-label="One fewer ${escapeHtml(of)}">&minus;</button>
      <input
        class="stock-check__count"
        type="number"
        inputmode="numeric"
        step="1"
        value="${escapeHtml(partValue(item, part))}"
        aria-label="Counted ${escapeHtml(of)}"
        data-count
      >
      <button type="button" class="stock-check__step" data-step="1" aria-label="One more ${escapeHtml(of)}">+</button>
    </div>`;
}

// The two inputs of a row, by part. A non-master has only the whole.
function countInput(row, part) {
  return row && row.querySelector(`[data-part="${part}"] [data-count]`);
}

function figureOf(row, part) {
  return row && row.querySelector(`[data-part="${part}"]`);
}

// Counts checked today, per section, kept locally so a reload on flaky shop wifi
// doesn't lose a walker's place. Never authoritative - the till is.
function progressKey(categoryId) {
  return `dc-stock-check:${new Date().toISOString().slice(0, 10)}:${categoryId}`;
}

function readProgress(categoryId) {
  try {
    return new Set(JSON.parse(localStorage.getItem(progressKey(categoryId)) || '[]'));
  } catch (e) {
    return new Set();
  }
}

function writeProgress(categoryId, set) {
  try {
    localStorage.setItem(progressKey(categoryId), JSON.stringify(Array.from(set)));
  } catch (e) {
    /* private browsing or a full quota - progress display is a nicety, not the job */
  }
}

class StockCheck extends HTMLElement {
  constructor() {
    super();
    this.url = this.dataset.url;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.byId = new Map(); // categoryId -> category
    this.childrenOf = new Map(); // parentId ('' for root) -> [category]
    this.path = []; // ancestor chain of the node on screen
    this.items = []; // the current node's OWN products
    this.checked = new Set();
    this.searching = false;

    // Only the newest navigation may render. Tapping another section while one
    // is still loading used to let the old reply arrive and paint its products
    // into whatever section you had moved on to.
    this.loadToken = 0;
    this.inflight = null;

    this.timers = new Map(); // stockId -> pending save timer
    this.queue = new Map(); // stockId -> save that failed and wants retrying

    this.panel = this.querySelector('[data-panel]');
    this.status = this.querySelector('[data-status]');
    this.searchInput = this.querySelector('[data-search]');
    this.searchClear = this.querySelector('[data-search-clear]');
    this.pending = this.querySelector('[data-pending]');
    this.pendingText = this.querySelector('[data-pending-text]');
    this.confirmDialog = this.querySelector('[data-signoff-dialog]');
  }

  connectedCallback() {
    this.panel.addEventListener('click', this.onPanelClick.bind(this));
    this.panel.addEventListener('change', this.onPanelChange.bind(this));
    // THE PICTURES WIRE THEMSELVES - a tap opens every picture side by side, and a desk gets
    // a hover preview on top. Delegated, because rows are rebuilt constantly here.
    window.StaffPictures.bind(this.panel);
    this.searchInput.addEventListener('input', this.onSearchInput.bind(this));
    this.searchClear.addEventListener('click', () => {
      this.searchInput.value = '';
      this.searchClear.hidden = true;
      this.searching = false;
      this.openNode(this.currentId());
    });
    // Both of these are evidence the situation has changed, so the give-up
    // counter starts again rather than the bar staying stuck on "gave up".
    const retryNow = () => {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.retryAttempts = 0;
      this.flushQueue();
    };

    this.querySelector('[data-retry]').addEventListener('click', retryNow);

    // The browser knows the wifi came back before any backoff timer does.
    this.onOnline = () => {
      if (this.queue.size) retryNow();
    };
    window.addEventListener('online', this.onOnline);

    // A count taken and not yet written is the one thing worth interrupting for.
    // Held counts are deliberately not persisted - Jonathan's call, 05/08/2026.
    this.onBeforeUnload = (event) => {
      if (this.timers.size === 0 && this.queue.size === 0) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', this.onBeforeUnload);

    // A section name clicked on the Categories tab lands here. Before the section list has
    // loaded, the id is held and opened once it arrives.
    document.addEventListener('dc:stock-open-section', (event) => {
      const id = event.detail && event.detail.id;
      if (id == null) return;
      this.searchInput.value = '';
      this.searchClear.hidden = true;
      if (this.byId.size) {
        this.openNode(this.byId.has(String(id)) ? id : null);
      } else {
        this.pendingOpen = String(id);
      }
    });

    // FETCHED WHEN THE TAB IS FIRST OPENED, not with the page (15/10/2026): every tab loading at
    // once queued six n8n calls behind each other and made all of them slow.
    (window.dcWhenShown || ((el, fn) => fn()))(this, () => this.loadCategories());
  }

  disconnectedCallback() {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.timers.forEach((t) => clearTimeout(t));
    clearTimeout(this.retryTimer);
    clearTimeout(this.searchTimer);
  }

  setStatus(message, isError) {
    this.status.textContent = message || '';
    this.status.hidden = !message;
    this.status.classList.toggle('stock-check__status--error', !!isError);
  }

  // Claim this navigation as the current one and cancel any request still in
  // flight for the previous one.
  beginLoad() {
    if (this.inflight) this.inflight.abort();
    this.inflight = new AbortController();
    return { token: ++this.loadToken, signal: this.inflight.signal };
  }

  currentId() {
    return this.path.length ? this.path[this.path.length - 1] : null;
  }

  childrenKey(id) {
    return id == null ? '' : String(id);
  }

  /* ---- the section tree ----------------------------------------------------- */

  async loadCategories() {
    const { token, signal } = this.beginLoad();
    this.panel.innerHTML = spinnerHtml('Loading\u2026');
    try {
      const data = await postOp(
        this.url,
        this.secret,
        'listCategories',
        { customerId: this.customerId },
        signal,
      );
      if (token !== this.loadToken) return;
      (data.categories || []).forEach((c) => {
        this.byId.set(String(c.id), c);
        const key = this.childrenKey(c.parentId);
        if (!this.childrenOf.has(key)) this.childrenOf.set(key, []);
        this.childrenOf.get(key).push(c);
      });
      // Sections a manager has flagged come first, wherever they sit in the tree,
      // then everything else alphabetically. The flag is set on the manager's own
      // page; here it only decides order and highlighting, so a staff member walking
      // the shop meets the sections that need counting before the rest.
      //
      // Ordering by name inside each group, rather than by how overdue a section is,
      // keeps the list stable between loads - someone half way down a shelf should
      // not find the tiles have rearranged themselves.
      this.childrenOf.forEach((list) =>
        list.sort((a, b) => {
          const flagged = Number(Boolean(b.priority)) - Number(Boolean(a.priority));
          if (flagged) return flagged;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }),
      );
      const wanted = this.pendingOpen;
      this.pendingOpen = null;
      this.openNode(wanted && this.byId.has(wanted) ? wanted : null);
    } catch (error) {
      this.setStatus(`Couldn't load the sections: ${error.message}`, true);
    }
  }

  // Rebuild the ancestor chain for a node, so a breadcrumb can be drawn from
  // any starting point (including one reached from a search result).
  chainFor(id) {
    const chain = [];
    let cursor = id == null ? null : String(id);
    while (cursor != null && this.byId.has(cursor)) {
      chain.unshift(cursor);
      const parent = this.byId.get(cursor).parentId;
      cursor = parent == null ? null : String(parent);
    }
    return chain;
  }

  async openNode(id) {
    const { token, signal } = this.beginLoad();
    this.searching = false;
    this.path = this.chainFor(id);
    const node = id == null ? null : this.byId.get(String(id));
    this.checked = readProgress(id == null ? 'root' : id);
    this.items = [];
    // Set BEFORE the first render, or the panel claims the section is empty for
    // the second or two the till takes to answer.
    this.loadingItems = !!(node && node.inStockHere > 0);

    // Draw the sub-sections straight away - they need no fetch, so a folder
    // opens instantly and only the product list has to wait on the till.
    this.render();

    if (this.loadingItems) {
      try {
        const data = await postOp(
          this.url,
          this.secret,
          'listStock',
          { customerId: this.customerId, categoryId: node.id },
          signal,
        );
        // Someone tapped another section while this was in flight. Drop it.
        if (token !== this.loadToken) return;
        this.items = data.items || [];
      } catch (error) {
        if (token !== this.loadToken) return;
        this.loadingItems = false;
        this.render();
        this.setStatus(`Couldn't load ${node.name}: ${error.message}`, true);
        return;
      }
      this.loadingItems = false;
    }
    this.render();
  }

  breadcrumbHtml() {
    if (!this.path.length) return '';
    const crumbs = ['<button type="button" data-node-id="">All sections</button>'].concat(
      this.path.map((id, index) => {
        const c = this.byId.get(id);
        const label = escapeHtml(c ? c.name : '?');
        return index === this.path.length - 1
          ? `<span aria-current="page">${label}</span>`
          : `<button type="button" data-node-id="${escapeHtml(id)}">${label}</button>`;
      }),
    );
    return `<nav class="stock-check__crumbs" aria-label="Section">${crumbs.join(
      '<span class="stock-check__crumb-sep" aria-hidden="true">\u203a</span>',
    )}</nav>`;
  }

  /* ---- signing a section off ------------------------------------------------ */

  // A sign-off covers THIS node's own products and nothing else - not the
  // sub-sections beneath it, not its parent. So it is only offered on a node
  // that actually holds products; a pure folder has nothing of its own to
  // vouch for, and offering the button there would imply it covered the
  // sections inside it.
  signOffHtml(node) {
    if (!node || this.loadingItems || !this.items.length) return '';
    const when = node.lastCheckedAt ? formatCheckedAt(node.lastCheckedAt) : '';
    const note = when
      ? `Last checked ${escapeHtml(when)}${node.lastCheckedBy ? ` by ${escapeHtml(node.lastCheckedBy)}` : ''}`
      : 'Not checked in full yet.';
    // Note on the left, button on the right: you read the state, then act on it.
    return `
      <div class="stock-check__signoff">
        <p class="stock-check__signoff-note${when ? ' is-checked-off' : ''}" data-sign-off-note>${note}</p>
        <button type="button" class="stock-check__signoff-button" data-sign-off>All Checked?</button>
      </div>`;
  }

  // Yes/No, not window.confirm()'s OK/Cancel - and a tablet browser can suppress
  // confirm() outright. Falls back to it only where <dialog> is missing.
  askSignOff() {
    const question = 'Are you sure that the stock count is accurate for all the products in this section?\n\n' +
      "Anything in this section that isn't on the list will be set to zero on the till and the website.";
    const dialog = this.confirmDialog;
    if (!dialog || typeof dialog.showModal !== 'function') {
      return Promise.resolve(window.confirm(question));
    }
    return new Promise((resolve) => {
      dialog.returnValue = '';
      const onClose = () => {
        dialog.removeEventListener('close', onClose);
        resolve(dialog.returnValue === 'yes'); // Esc closes with '', i.e. No
      };
      dialog.addEventListener('close', onClose);
      dialog.showModal();
    });
  }

  async signOffCurrent() {
    const id = this.currentId();
    const node = id == null ? null : this.byId.get(String(id));
    if (!node) return;

    // Vouching for a section while counts from it are still unwritten would be
    // signing off numbers the till has not accepted.
    if (this.timers.size || this.queue.size) {
      this.setStatus("Some counts haven't reached the till yet \u2014 wait for those, then sign off.", true);
      return;
    }

    if (!(await this.askSignOff())) return;

    const button = this.panel.querySelector('[data-sign-off]');
    if (button) {
      button.disabled = true;
      button.textContent = 'Saving\u2026';
    }
    try {
      // Deliberately not abortable and not tied to the navigation token: a
      // sign-off is a write, like a count, and must not be cancelled by someone
      // tapping another section while it is in flight.
      const data = await postOp(this.url, this.secret, 'signOff', {
        customerId: this.customerId,
        categoryId: node.id,
        categoryName: node.name,
      });
      node.lastCheckedAt = data.checkedAt;
      node.lastCheckedBy = data.staffName;
      // WHAT HAPPENED TO THE PRODUCTS THE LIST LEFT OUT (14/09/2026). The backend works
      // them out itself and sets them to zero; a refusal means the sign-off was recorded
      // but nothing was zeroed, and the person needs to know that rather than assume it.
      const zeroed = Number(data.confirmedZero) || 0;
      if (data.zeroRefused) {
        this.setStatus(`Signed off, but nothing was set to zero: ${data.zeroRefused}`, true);
      } else if (zeroed) {
        this.setStatus(`Signed off. ${zeroed} product${zeroed === 1 ? '' : 's'} not on the list ` +
          `${zeroed === 1 ? 'is' : 'are'} now zero on the till; the website follows within half an hour.`);
      } else {
        this.setStatus('');
      }
      // Only repaint if they are still standing in the section they signed off.
      if (String(this.currentId()) === String(node.id) && !this.searching) this.render();
    } catch (error) {
      this.setStatus(`Couldn't record the sign-off: ${error.message}`, true);
      if (button) {
        button.disabled = false;
        button.textContent = 'All Checked?';
      }
    }
  }

  // Sections a manager has asked for next, pulled out of the tree and put at the very
  // top of the front page. Sorting them first within their own branch is not enough:
  // a prioritised section can sit three levels down, and nobody browses to something
  // they do not already know is waiting for them. Tapping one jumps straight there,
  // the same jump a search result makes.
  //
  // FRONT PAGE ONLY. Once someone has drilled into a section they are counting, and
  // repeating the list above every screen would push the actual products down the
  // page on the one view where the tablet is being held in one hand.
  prioritiesHtml(currentId) {
    if (currentId != null) return '';
    const flagged = [];
    this.byId.forEach((node) => {
      if (node.priority) flagged.push(node);
    });
    if (!flagged.length) return '';
    flagged.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    return `
      <section class="stock-check__priorities">
        <h2 class="stock-check__priorities-heading">Priorities</h2>
        <ul class="stock-check__categories">
          ${flagged
            .map(
              (c) => `
            <li>
              <button type="button" class="stock-check__category is-priority" data-node-id="${escapeHtml(c.id)}">
                <span class="stock-check__category-name">${escapeHtml(c.name)}</span>
                <span class="stock-check__category-flag">Priority</span>
                <!-- A priority asks for the section's OWN products to be counted, never its
                     sub-sections' (Jonathan, 31/10/2026), so this tile counts those alone. -->
                <span class="stock-check__category-count">${escapeHtml(c.inStockHere)} in stock</span>
              </button>
            </li>`,
            )
            .join('')}
        </ul>
      </section>`;
  }

  render() {
    const id = this.currentId();
    const node = id == null ? null : this.byId.get(String(id));
    const children = this.childrenOf.get(this.childrenKey(id)) || [];

    // No running tally of rows counted - Jonathan's call, 05/08/2026: it said
    // nothing useful, and a section being finished is now a deliberate sign-off
    // rather than something inferred from how many rows have been touched.
    if (!node || this.loadingItems || this.items.length) {
      this.setStatus('');
    } else {
      this.setStatus(`${node.name} \u2014 pick a section`);
    }

    let html = this.breadcrumbHtml();
    html += this.prioritiesHtml(id);

    if (children.length) {
      // Only on the front page. Once someone has drilled in, the breadcrumb already
      // says where they are, and a fixed heading would contradict it.
      if (id == null) {
        html += '<h2 class="stock-check__sections-heading">Till Sections</h2>';
      }
      html += `<ul class="stock-check__categories">${children
        .map(
          (c) => `
        <li>
          <button
            type="button"
            class="stock-check__category${c.priority ? ' is-priority' : ''}"
            data-node-id="${escapeHtml(c.id)}"
          >
            <span class="stock-check__category-name">${escapeHtml(c.name)}</span>
            ${
              c.priority
                ? '<span class="stock-check__category-flag">Priority</span>'
                : ''
            }
            <span class="stock-check__category-count">${escapeHtml(c.inStockTotal)} in stock</span>
          </button>
        </li>`,
        )
        .join('')}</ul>`;
    }

    if (this.items.length) {
      // Only label the list when sub-sections sit above it, otherwise the
      // heading is stating the obvious.
      if (children.length) {
        html += `<h2 class="stock-check__subhead">Also in this section</h2>`;
      }
      // Sits immediately above the products it vouches for. On a plain section
      // that puts it directly under the breadcrumbs; where sub-sections exist it
      // stays below their tiles, because a sign-off covers this node's own
      // products and never the sections nested inside it - and a button sitting
      // above those tiles would say otherwise.
      html += this.signOffHtml(node);
      html += this.rowsHtml(this.items);
    } else if (this.loadingItems) {
      html += spinnerHtml('Loading\u2026');
    } else if (!children.length) {
      html += '<p class="stock-check__empty">Nothing in stock here.</p>';
    }

    this.panel.innerHTML = html;
  }

  rowsHtml(items) {
    if (!items.length) return '<p class="stock-check__empty">Nothing to show here.</p>';
    return `<ul class="stock-check__list">${items.map((i) => this.rowHtml(i)).join('')}</ul>`;
  }


  rowHtml(item) {
    const isChecked = this.checked.has(String(item.stockId));
    const recent = isRecentlyChecked(item.lastCheckedAt);
    const codes = splitCodes(item.barcode);
    const extra = codes.length - 1;
    return `
      <li class="stock-check__row${isChecked ? ' is-checked' : ''}${recent ? ' is-recent' : ''}" data-stock-id="${escapeHtml(item.stockId)}">
        <div class="stock-check__image">${imageHtml(item)}</div>
        <div class="stock-check__info">
          <span class="stock-check__name">${escapeHtml(item.name)}</span>
          ${
            codes.length
              ? `<span class="stock-check__codes" data-codes>${codes
                  .map((code) => `<span class="stock-check__code">${escapeHtml(code)}</span>`)
                  .join('')}${
                  extra > 0
                    ? `<button type="button" class="stock-check__more" data-more="${extra}" aria-expanded="false">+${extra} more</button>`
                    : ''
                }</span>`
              : ''
          }
          <span class="stock-check__note" data-row-note hidden></span>
        </div>
        <div class="stock-check__controls">
          <div class="stock-check__figures${isMaster(item) ? ' is-split' : ''}">
            ${figureHtml(item, 'whole')}
            ${isMaster(item) ? figureHtml(item, 'unit') : ''}
          </div>
          ${
            // No stock row means there is nothing to confirm against - only the
            // manager's high-demand search ever produces one, but a confirm that
            // could never be recorded should not be offered.
            item.stockId
              ? `<div class="stock-check__confirm-cell">
                  <button type="button" class="stock-check__confirm" data-confirm aria-label="Confirm ${escapeHtml(item.name)} is correct">Confirm</button>
                  <span class="stock-check__confirmed-by" data-confirmed-by>${
                    // Only while the check still counts. The name disappearing is
                    // half of how a row says "this needs looking at again" - a
                    // stale name beside a no-longer-green box would say the
                    // opposite of what the colour says.
                    recent ? escapeHtml(item.lastCheckedBy || '') : ''
                  }</span>
                </div>`
              : ''
          }
        </div>
      </li>`;
  }

  /* ---- search ------------------------------------------------------------- */

  onSearchInput() {
    const term = this.searchInput.value.trim();
    this.searchClear.hidden = term === '';
    clearTimeout(this.searchTimer);
    if (term.length < MIN_SEARCH) {
      if (this.searching) this.openNode(this.currentId());
      return;
    }
    this.searchTimer = setTimeout(() => this.runSearch(term), SEARCH_DELAY);
  }

  async runSearch(term) {
    const { token, signal } = this.beginLoad();
    this.searching = true;
    this.setStatus('');
    this.panel.innerHTML = spinnerHtml(`Searching for \u201c${term}\u201d\u2026`);
    try {
      const data = await postOp(
        this.url,
        this.secret,
        'search',
        { customerId: this.customerId, query: term },
        signal,
      );
      if (token !== this.loadToken) return;
      this.items = data.items || [];
      this.setStatus(
        this.items.length
          ? `${this.items.length} match${this.items.length === 1 ? '' : 'es'} for \u201c${term}\u201d`
          : `Nothing matched \u201c${term}\u201d. Try fewer letters, or more barcode digits.`,
      );
      this.panel.innerHTML = this.rowsHtml(this.items);
    } catch (error) {
      if (token !== this.loadToken) return;
      this.setStatus(`Search failed: ${error.message}`, true);
    }
  }

  /* ---- editing ------------------------------------------------------------ */

  onPanelClick(event) {
    // A tap on a picture belongs to the shared gallery, not to the row beneath it.
    if (event.target.closest('[data-staff-pictures]')) return;

    if (event.target.closest('[data-sign-off]')) {
      this.signOffCurrent();
      return;
    }
    const nodeButton = event.target.closest('[data-node-id]');
    if (nodeButton) {
      const raw = nodeButton.dataset.nodeId;
      this.openNode(raw === '' ? null : raw);
      return;
    }
    const more = event.target.closest('[data-more]');
    if (more) {
      const codes = more.closest('[data-codes]');
      const expanded = codes.classList.toggle('is-expanded');
      more.setAttribute('aria-expanded', String(expanded));
      more.textContent = expanded ? 'Show fewer' : `+${more.dataset.more} more`;
      return;
    }
    const tick = event.target.closest('[data-confirm]');
    if (tick) {
      this.confirmRow(tick.closest('[data-stock-id]'));
      return;
    }
    const step = event.target.closest('[data-step]');
    if (!step) return;
    const row = step.closest('[data-stock-id]');
    const figure = step.closest('[data-part]');
    const input = figure.querySelector('[data-count]');
    const next = Number(input.value || 0) + Number(step.dataset.step);
    input.value = next;
    // ONLY THE FIGURE THAT WAS TAPPED GOES AMBER, and later green (his rule).
    // The other one has not been counted and must not look as though it has -
    // that is the whole point of splitting the row.
    this.scheduleSave(row, figure);
  }

  onPanelChange(event) {
    const input = event.target.closest('[data-count]');
    if (input) this.scheduleSave(input.closest('[data-stock-id]'), input.closest('[data-part]'));
  }

  // Wait for the tapping to stop before writing, so "+ + + + +" is one write.
  scheduleSave(row, figure) {
    const stockId = row.dataset.stockId;
    const item = this.items.find((i) => String(i.stockId) === String(stockId));
    if (!item) return;
    if (figure) {
      figure.classList.add('is-dirty');
      figure.classList.remove('is-saved');
    }
    row.classList.remove('is-failed', 'is-conflict');
    clearTimeout(this.timers.get(stockId));
    this.timers.set(
      stockId,
      setTimeout(() => {
        this.timers.delete(stockId);
        const whole = countInput(row, 'whole');
        const unit = countInput(row, 'unit');
        this.commit({
          stockId: item.stockId,
          productId: item.productId,
          // What the tablet believed before this edit. The server refuses the
          // write if the till has moved since, rather than eating a real sale.
          expected: item.stock,
          value: Number(whole ? whole.value : item.stock) || 0,
          // BOTH FIGURES TRAVEL TOGETHER on a master, whichever one was tapped.
          // One till record holds them both and the till has no partial update,
          // so it is PUT BACK WHOLE - sending only the figure that changed would
          // mean the other travels as whatever the server last read, which is
          // exactly how a stale loose count gets written over a fresh one.
          //
          // Each still carries its OWN expected value, so a booster sold while
          // the shelf was being walked refuses the write rather than being
          // erased - the same guard the box count has always had.
          ...(isMaster(item) && unit
            ? {
              volume: Number(unit.value) || 0,
              expectedVolume: Number(item.volume) || 0,
              // The server's own pack size making a round trip. It is what
              // normalises the till's batch split; if it were ever wrong the
              // figures would stop matching what this page displayed and the
              // count would be refused rather than written.
              volumeOfSale: Number(item.volumeOfSale) || 0,
            }
            : {}),
        });
      }, SAVE_DELAY),
    );
  }

  // THE TICK: the shelf agrees with the screen, so nothing changes and the row
  // simply goes green.
  //
  // It is NOT a shortcut past the safety check. The server re-reads the till and
  // refuses if the number has moved since this screen drew it - a tick recorded
  // against a figure a sale had already overtaken would be a false claim about
  // the shelf, which is worse than no tick at all. So a conflict lands here the
  // same way it lands on a count, and the same sentence is shown.
  //
  // Green goes on immediately rather than waiting for the round trip: staff walk
  // a shelf at their own pace and a button that does nothing for a second gets
  // pressed twice. It comes off again if the server refuses.
  async confirmRow(row) {
    if (!row || row.dataset.confirming === '1') return;
    const stockId = row.dataset.stockId;
    const item = this.items.find((i) => String(i.stockId) === String(stockId));
    if (!item) return;

    // A count still waiting to be sent must settle first, or the two race and
    // whichever lands last decides - and the tick would be confirming a number
    // the till has not been told about yet.
    if (this.timers.has(stockId) || this.queue.has(stockId)) {
      this.setNote(row, 'Still saving your count - the tick will be free in a moment.');
      return;
    }

    const wasRecent = row.classList.contains('is-recent');
    row.dataset.confirming = '1';
    row.classList.add('is-recent');
    row.classList.remove('is-conflict', 'is-failed');
    this.setNote(row, '');

    try {
      const data = await postOp(this.url, this.secret, 'confirmStock', {
        customerId: this.customerId,
        stockId: item.stockId,
        productId: item.productId,
        // Both are what the screen is showing. `expected` is what the server
        // compares the till against; `value` exists because the shared conflict
        // check validates it, and a tick changes nothing, so they are the same.
        expected: item.stock,
        value: item.stock,
        // THE TICK CONFIRMS BOTH FIGURES (his rule): *"The confirm button should
        // turn both master and sub product values green."* It says the record on
        // screen is right, and on a master the record is two numbers - so the
        // loose count is sent, unchanged, with its own expected value. A booster
        // sold while the shelf was being walked refuses the tick exactly as it
        // refuses a count, because a tick against an overtaken figure is a false
        // claim about the shelf.
        ...(isMaster(item)
          ? {
            volume: item.volume,
            expectedVolume: item.volume,
            volumeOfSale: item.volumeOfSale,
          }
          : {}),
      });

      if (data.conflict) {
        item.stock = data.stock;
        if (data.volume != null) item.volume = data.volume;
        this.showFigures(row, data);
        if (!wasRecent) row.classList.remove('is-recent');
        row.classList.add('is-conflict');
        this.setNote(row, `Sold while you were counting — till now says ${data.stock}. Re-count this one.`);
        return;
      }

      item.lastCheckedAt = data.checkedAt || new Date().toISOString();
      item.lastCheckedBy = data.staffName || '';
      this.setConfirmedBy(row, item.lastCheckedBy);
      if (data.stock != null) item.stock = data.stock;
      if (data.volume != null) item.volume = data.volume;
      this.showFigures(row, data);
      // BOTH GO GREEN, and any amber is cleared: the tick has just told the till
      // that everything on this row is right, so a half-counted figure left
      // sitting amber would contradict the thing that was just confirmed.
      for (const part of ['whole', 'unit']) {
        const figure = figureOf(row, part);
        if (figure) figure.classList.remove('is-dirty', 'is-saved');
      }
    } catch (error) {
      // Nothing is queued for retry: unlike a count, a tick carries no number
      // anybody would lose, and re-sending it later would record a check made at
      // a moment nobody was looking at the shelf.
      if (!wasRecent) row.classList.remove('is-recent');
      row.classList.add('is-failed');
      this.setNote(row, "Couldn't reach the till - tap the tick again in a moment.");
    } finally {
      delete row.dataset.confirming;
    }
  }

  // The row may not be on screen - staff move on while a save is still being
  // retried - so every save works from a self-contained record and only touches
  // the DOM if that row still happens to be rendered.
  rowFor(stockId) {
    return this.panel.querySelector(`[data-stock-id="${CSS.escape(String(stockId))}"]`);
  }

  // Put the till's own figures on screen. Both, where there are two - the record
  // is read and written whole, so a response describes the whole record and
  // updating half of it would leave the other half showing what the tablet
  // guessed rather than what the till holds.
  showFigures(row, data) {
    const whole = countInput(row, 'whole');
    if (whole && data.stock != null) whole.value = data.stock;
    const unit = countInput(row, 'unit');
    if (unit && data.volume != null) unit.value = data.volume;
  }

  // Who last said this number was right. Cleared alongside the green, because
  // the two are one statement: a name with no green would claim a check that has
  // expired.
  setConfirmedBy(row, name) {
    const cell = row && row.querySelector('[data-confirmed-by]');
    if (cell) cell.textContent = name || '';
  }

  // Messages live on their own line rather than replacing the barcodes, so a
  // failed save no longer wipes them and there is nothing to restore afterwards.
  setNote(row, text) {
    const note = row && row.querySelector('[data-row-note]');
    if (!note) return;
    note.textContent = text || '';
    note.hidden = !text;
  }

  async commit(record) {
    const stockId = String(record.stockId);

    // Never queue something the till will refuse every time.
    const problem = countProblem(record.value);
    if (problem) {
      this.queue.delete(stockId);
      const badRow = this.rowFor(stockId);
      if (badRow) {
        // The whole record is refused, so no figure on it is amber or green any
        // more - a number the till will never take must not sit there looking as
        // though it was accepted.
        for (const part of ['whole', 'unit']) {
          const figure = figureOf(badRow, part);
          if (figure) figure.classList.remove('is-dirty', 'is-saved');
        }
        badRow.classList.remove('is-failed');
        badRow.classList.add('is-conflict');
        this.setNote(badRow, problem);
      }
      this.renderPending();
      return;
    }

    try {
      const data = await postOp(this.url, this.secret, 'setStock', {
        customerId: this.customerId,
        stockId: record.stockId,
        productId: record.productId,
        expected: record.expected,
        value: record.value,
        ...(record.volume != null
          ? {
            volume: record.volume,
            expectedVolume: record.expectedVolume,
            volumeOfSale: record.volumeOfSale,
          }
          : {}),
      });

      this.queue.delete(stockId);
      const item = this.items.find((i) => String(i.stockId) === stockId);
      if (item) {
        item.stock = data.stock;
        if (data.volume != null) item.volume = data.volume;
      }
      const row = this.rowFor(stockId);

      if (data.conflict) {
        // Somebody sold one while this section was being counted. The till's own
        // figures replace both, because the record was re-read whole.
        if (row) {
          this.showFigures(row, data);
          for (const part of ['whole', 'unit']) {
            const figure = figureOf(row, part);
            if (figure) figure.classList.remove('is-dirty', 'is-saved');
          }
          row.classList.remove('is-failed');
          row.classList.add('is-conflict');
          this.setNote(row, `Sold while you were counting \u2014 till now says ${data.stock}. Re-count this one.`);
        }
        this.renderPending();
        return;
      }

      // A COUNT IS A CHECK. The server records one against this product for the
      // same reason the tick does, so the row must go green here too - otherwise
      // counting a shelf would leave every row you actually touched looking
      // unchecked until the next reload.
      if (item) {
        item.lastCheckedAt = data.checkedAt || new Date().toISOString();
        item.lastCheckedBy = data.staffName || '';
      }
      if (row) {
        this.showFigures(row, data);
        // GREEN GOES ON THE FIGURE THAT WAS COUNTED, not on both. A count sends
        // the whole record because the till has no partial update, but only the
        // figure somebody actually touched has been counted - and saying the
        // other one has been is the false claim about a shelf this page exists
        // to avoid. `is-recent` still greens both, because a count IS a check
        // and a check is recorded against the record.
        for (const part of ['whole', 'unit']) {
          const figure = figureOf(row, part);
          if (figure && figure.classList.contains('is-dirty')) {
            figure.classList.remove('is-dirty');
            figure.classList.add('is-saved');
          }
        }
        row.classList.remove('is-failed', 'is-conflict');
        row.classList.add('is-recent');
        this.setConfirmedBy(row, item ? item.lastCheckedBy : '');
        this.setNote(row, '');
      }
      // Remembered so a reload on flaky shop wifi still shows which rows have
      // been counted today; no longer totalled anywhere.
      this.checked.add(stockId);
      if (!this.searching) {
        const id = this.currentId();
        writeProgress(id == null ? 'root' : id, this.checked);
      }
    } catch (error) {
      // Hold it and keep the number on screen; nothing counted is thrown away
      // while the tab is open. Retrying happens on its own from here.
      this.queue.set(stockId, record);
      const row = this.rowFor(stockId);
      if (row) {
        // AMBER COMES OFF BUT NOTHING GOES GREEN. The count is held and will be
        // sent on its own, and the red on the row is what says so; leaving a
        // figure amber as well would read as "still being typed" rather than
        // "waiting to reach the till".
        for (const part of ['whole', 'unit']) {
          const figure = figureOf(row, part);
          if (figure) figure.classList.remove('is-dirty');
        }
        row.classList.add('is-failed');
        this.setNote(
          row,
          "Couldn't reach the till. Your count is saved here and will be sent "
            + 'automatically - leave it with us, and do not re-count this one.',
        );
      }
      this.scheduleRetry();
    }
    this.renderPending();
  }

  // Keep trying quietly in the background, backing off so a dead connection
  // isn't hammered. Staff should not have to notice that the wifi came back.
  scheduleRetry() {
    if (this.retryTimer || this.queue.size === 0) return;
    this.retryAttempts = (this.retryAttempts || 0) + 1;
    if (this.retryAttempts > MAX_RETRY_ATTEMPTS) return; // say so instead of spinning
    const delay = Math.min(30000, 4000 * Math.pow(2, this.retryAttempts - 1));
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.flushQueue();
    }, delay);
  }

  renderPending() {
    const count = this.queue.size;
    if (count === 0) {
      this.retryAttempts = 0;
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.pending.hidden = true;
      return;
    }
    // A single blip recovers on its own within seconds, so don't interrupt for
    // it. The bar appears only once it is clear the till really is unreachable.
    this.pending.hidden = (this.retryAttempts || 0) < 2;
    const label = `${count} count${count === 1 ? '' : 's'} not saved yet`;
    this.pendingText.textContent =
      (this.retryAttempts || 0) > MAX_RETRY_ATTEMPTS
        ? `${label} \u2014 gave up trying. Tell a manager.`
        : `${label} \u2014 still trying.`;
  }

  async flushQueue() {
    if (this.flushing) return;
    this.flushing = true;
    try {
      for (const record of Array.from(this.queue.values())) {
        await this.commit(record);
      }
    } finally {
      this.flushing = false;
    }
    if (this.queue.size) this.scheduleRetry();
    this.renderPending();
  }
}

if (!customElements.get('stock-check')) {
  customElements.define('stock-check', StockCheck);
}
})();
